import React, { useCallback, useRef } from 'react';
import { Prompt, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showToast } from '@klpkonsern/ui';
import { useDebouncedEffect, validateValuesSync } from '../utils';
import {
    updateKontraktsforvaltningProcessFormfieldsCache,
    useSaveKontraktsforvaltningProcessFormfieldsMutation,
} from '../services/rtkApi';
import type { FormField } from '../interfaces';
import { useFormikContext } from 'formik';
import type { ISchema } from 'yup';

import type { IContractToRegistrationProcessActivity } from '@klp/contractregistration/classes/ContractToRegistrationProcessResponse';
import {
    resetformFieldProcessing,
    setformFieldProcessing,
    updateActivity,
} from '@klp/contractregistration/contractToRegistrationSlice';
import {
    contractToRegistrationSlice,
    useSignalR,
} from '@klp/contractregistration/index';
import type { FormValuesType } from './FormContainer';

type AutoSaveFormfieldsProps = {
    activity: IContractToRegistrationProcessActivity;
    fields: Array<FormField>;
    isDisabled: boolean;
    // Schema without required rules: blocks saving invalid values, not drafts
    validationSchema: ISchema<unknown>;
};

export const AutoSaveFormfields = ({
    activity,
    fields,
    isDisabled,
    validationSchema,
}: AutoSaveFormfieldsProps) => {
    const { t, i18n } = useTranslation();
    const { resetForm, values, dirty, errors, touched, setTouched } =
        useFormikContext<FormValuesType>();
    const dispatch = useDispatch();

    const errorsRef = useRef(errors);
    const touchedRef = useRef(touched);
    errorsRef.current = errors;
    touchedRef.current = touched;
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const [saveForm, { isLoading }] =
        useSaveKontraktsforvaltningProcessFormfieldsMutation();
    const { connected } = useSignalR();
    const { setFormFieldErrors } = contractToRegistrationSlice.actions;

    const handleSaveForm = async () => {
        // Gate on the exact values being saved. Formik's errors state only
        // updates on submit (validateOnChange is off) and lags behind values,
        // so it can't tell whether this snapshot is safe to save.
        const hasErrors =
            Object.keys(validateValuesSync(values, validationSchema)).length >
            0;
        dispatch(setFormFieldErrors(hasErrors));

        if (!activity.id || isDisabled || hasErrors) return;

        const updatedFormFields = fields.map((field) => ({
            ...field,
            value: values?.[field?.id as string],
        }));
        const payload = {
            oppgaveId: activity.id,
            formFields: updatedFormFields,
        };

        const updatedActivity = {
            ...activity,
            formFields: updatedFormFields,
        };

        resetForm({
            values,
            errors: errorsRef.current,
            touched: touchedRef.current,
        });
        dispatch(setformFieldProcessing());

        await saveForm(payload)
            .then(() => {
                dispatch(
                    updateKontraktsforvaltningProcessFormfieldsCache(
                        kontraktId,
                        i18n.language,
                        updatedFormFields,
                        activity.id,
                        connected,
                    ),
                );

                dispatch(updateActivity(updatedActivity));
            })
            .catch(() => {
                showToast({
                    body: t('contractRegistration.errorSaveForm'),
                    type: 'error',
                });
            })
            .finally(() => {
                setTouched(touchedRef.current);
                dispatch(resetformFieldProcessing());
            });
    };

    const debouncedSaveForm = useCallback(async () => {
        if (isDisabled || isLoading || !dirty) {
            return;
        }

        await handleSaveForm();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, isLoading]);

    useDebouncedEffect(debouncedSaveForm, [values], 1000);

    return (
        <Prompt
            when={dirty && !isDisabled}
            message={() => {
                handleSaveForm();
                return true;
            }}
        />
    );
};
